define((require) => {
    "use strict";

    const _                          = require("underscore");
    const CommentCollection          = require("ui/comments/CommentCollection");
    const ConcatenatedCollection     = require("zbone/ConcatenatedCollection");
    const EditorEvents               = require("shared/services/EditorEvents");
    const ExperienceModel            = require("shared/ui/experience/ExperienceModel");
    const ImprovedModel              = require("shared/zbone/ImprovedModel");
    const LayerEnum                  = require("ui/editor/LayerEnum");
    const LoggedInUserState          = require("shared/ui/user/LoggedInUserState");
    const NotificationBar            = require("editor/ui/notificationbar/NotificationBar");
    const Rectangle                  = require("shared/util/Rectangle");
    const Storage                    = require("shared/services/Storage");
    const Time                       = require("shared/services/Time");
    const UserCollection             = require("ui/user/UserCollection");
    const ZoomModel                  = require("ui/editor/ZoomModel");
    const WidgetSelectionViewModel   = require("ui/editor/WidgetSelectionViewModel");

    /**
     * View model for the editor view.
     *
     * <p>The model must be initialized with an experience model. Other properties are optional.</p>
     *
     * @class EditorViewModel
     * @constructor
     */
    return ImprovedModel.extend({

        /**
         * The entity to keep track of widget selections.
         * @see WidgetSelectionViewModel
         */
        selection: null,

        transient: [
            "isSelectingElements",
            "isProcessingEmail",
            "hasOverlappedEmailWidgets"
        ],


        defaults: {
            currentLayer: LayerEnum.SCENE,
            experience: null,
            sceneNumber: 1,
            focusedView: "canvas",
            draggingWidget: false,
            isSelectingElements: false,
            preventSelectionChange: false,

            // Currently we don't need to listen to changes inside this object, so we aren't modelling it as a
            // relational model
            widgetPropertyViewsState: {},

            rulersShown: true,
            scenesNavigatorCollapsed: false,
            snappingEnabled: false,
            showComments: true,
            showMasterLayers: true,

            emailModeEnabled: false,
            isProcessingEmail: false,
            hasOverlappedEmailWidgets: false,

            // Comment-related stuff
            commentsRenderer: null,
            experienceComments: null, // When loaded, map of scene ID to CommentCollection,
            sceneComments: new ConcatenatedCollection(),
            comments: null, // Collection containing all comments
            lastVisit: {visit: 0, sceneVisits: {}},
            zoomModel: new ZoomModel(),

            // In case this model gets passed to the view layer - expose the 'viewportVisibility' (defaults to cover all)
            viewportVisibility: new Rectangle(0, 0, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),

            // The scroll offset on Y axis. This is how much of the content is above the scrollable area.
            contentOffsetY: 0,

            /**
             * Whether image tagging is enabled for the logged in user.
             *
             * @type {Boolean}
             */
            imageTaggingEnabled: false,

            /**
             *
             * When inventory tagged is enabled for the logged in user
             *
             * @type {Boolean}
             */
            inventoryEnabled: false,


            /**
             * @type {Boolean}
             */
            imageViewedEventsEnabled: false,

            /**
             * @type {ImageLibrarySorting}
             * @see ImageLibrarySortingProvider
             */
            imageLibrarySorting: null,

            /**
             * The current `TemplateModel`, if any.
             *
             * @type {TemplateModel}
             */
            templateModel: null
        },

        computed: {
            /**
             * The scene the user is currently on.
             * Note, this always returns the scene model; if the user is the overlay or background layers, this will still return the scene model
             * (See activeLayerSceneModel)
             */
            scene: {
                deps: ["sceneNumber"],
                value(sceneNumber) {
                    return this.get("experience").getSceneBySceneNumber(sceneNumber);
                }
            },
            /**
             * The model for the scene or layer the user is currently on.
             */
            activeLayerSceneModel: {
                deps: ["currentLayer", "scene"],
                value(layer, scene) {
                    switch (layer) {
                        case LayerEnum.OVERLAY:
                            return this.get("experience").get("overlayScene");
                        case LayerEnum.BACKGROUND:
                            return this.get("experience").get("backgroundScene");
                        case LayerEnum.SCENE:
                            return scene;
                        default:
                            throw new Error(`unrecognised layer '${layer.toString()}'`);
                    }
                }
            }
        },

        deleteSelection(options) {
            const experience = this.get("experience");
            const project = this.get("project");

            this.selection.get("widgets").forEach(({model}) => {
                // If the model has a subtype that is not empty, it is a subwidget
                if (model.get("subtype")) {
                    // Cannot delete the subwidget - do nothing

                } else {
                    model.destroy();

                    const eventProperties = {
                        projectId: project && project.get("id"),
                        widgetType: model.get("type"),
                        experienceId: experience.get("id"),
                        experienceVersion: experience.get("version"),
                        experiencePushedVersion: experience.get("pushedVersion"),
                        triggerOrigin: options.triggerOrigin
                    };

                    EditorEvents.trigger(EditorEvents.constants.DELETE_WIDGET, eventProperties);
                }
            });

            this.selection.clear();
        },

        // This is the same with computed Scene property
        // The reason we need this is to avoid that when sceneNumber changes, both isBackgroundScene and Scene
        // properties will be recomputed in order. But any listeners on isBackgroundScene will not be able to
        // get correct scene as the scene is not computed at that point.
        getCurrentScene() {
            return this.get("experience").getSceneBySceneNumber(this.get("sceneNumber"));
        },

        getCurrentSceneIndex() {
            return this.getCurrentScene().getSceneIndex();
        },

        isOnBackgroundLayer() {
            return this.get("currentLayer") === LayerEnum.BACKGROUND;
        },

        isOnOverlayLayer() {
            return this.get("currentLayer") === LayerEnum.OVERLAY;
        },

        isOnSceneLayer() {
            return this.get("currentLayer") === LayerEnum.SCENE;
        },

        /**
         * Sets the scene number, triggering a change event even if the scene number hasn't changed. This is useful when
         * the scene referenced by the scene number has changed without the scene number itself changing (via an added
         * or removed scene), which conceptually should be treated as a scene number change by our listeners.
         *
         * @param {String|Number} sceneNumber The new scene number.
         * @private
         */
        _forceSetSceneNumber(sceneNumber) {
            if (this.get("sceneNumber") !== sceneNumber) {
                this.set("sceneNumber", sceneNumber);
            } else {
                this.trigger("change:sceneNumber", this, sceneNumber, {});
            }
        },

        _sceneChangeHandler() {
            // Register scene visit
            LoggedInUserState.registerSceneVisit(this.get("experience").get("id"), this.get("scene").get("id"));
        },

        _commentRead(comment) {
            if (comment.get("unread") > 0) {
                // Update counters
                this.get("experience").set("unread", this.get("experience").get("unread") - comment.get("unread"), {computedProperty: true});
                this.get("scene").set("unread", this.get("scene").get("unread") - comment.get("unread"), {computedProperty: true});
                comment.set("unread", 0, {computedProperty: true});

                // Update comment flags
                comment.set("isUnread", false, {computedProperty: true});
                comment.get("replies").each((reply) => {
                    reply.set("isUnread", false, {computedProperty: true});
                });
            }
        },

        _fetchLastCommentedAt(sceneId) {
            // Currently the backend can't ignore the value of lastCommentedAt we send when saving an experience
            // As a result, we need to ensure we keep the values in sync on comment post and delete
            const experience = new ExperienceModel({id: this.get("experience").get("id")});
            experience.fetch().done(() => {
                this.get("experience").set("lastCommentedAt", experience.get("lastCommentedAt"), {triggeredByServerUpdate: true});
                this.get("experience").getSceneById(sceneId).set("lastCommentedAt", experience.getSceneById(sceneId).get("lastCommentedAt"), {triggeredByServerUpdate: true});
            });
        },

        _updateWidgetTabOrder(experience) {
            let overlay = experience.get("overlayScene");
            let backgroundScene = experience.get("backgroundScene");
            let scenes = experience.get("scenes");

            const secondaryLayers = [overlay, backgroundScene];
            const fieldsToFix = ["widgetTabOrder", "removedWidgetTabOrder"];

            // we need to check each scene and move data to the rest layers if it is needed
            scenes.forEach((scene) => {
                fieldsToFix.forEach((field) => {
                    scene.get(field)
                        // filter scene's widgets in order to find widgets which don't belong to scene
                        .filter((widgetUUID) => !scene.get("widgets").find((widget) => widget.get("uuid") === widgetUUID))
                        .forEach((widgetUUID) => {
                        // looking for the layer to which the widget belongs
                            secondaryLayers.forEach((layer) => {
                                const appropriateWidget = layer.get("widgets").find((widget) => widget.get("uuid") === widgetUUID);
                                /* when appropriateWidget is found in another layer,
                                   appropriateWidget's data must be moved to the correct layer */
                                if (appropriateWidget) {
                                    // delete item from scene's field
                                    const sceneOrderData = [...scene.get(field)].filter((tabOrderId) => {
                                        return tabOrderId !== widgetUUID;
                                    });
                                    // add item to correct layer's field
                                    const layerOrderData = [...layer.get(field)];

                                    // check if current layer doesn't contain widgetUUID, because each scene can contain the same data
                                    if (!layerOrderData.includes(widgetUUID)) {
                                        layerOrderData.push(widgetUUID);
                                    }

                                    scene.set(field, sceneOrderData);
                                    layer.set(field, layerOrderData);
                                }
                            });
                        });
                });
            });
        },

        // TODO: We need to break down this giant method, hard to read.
        initialize() {
            const experience = this.get("experience");
            const scene = this.get("scene");
            const experienceId = experience.get("id");

            // Experience and valid scene is required
            if (!experience || typeof scene === "undefined") {
                NotificationBar.error("A valid experience id and scene number is required.", {raise: true});
            }

            // Fix "widgetTabOrder" and "removedWidgetTabOrder" data for layers
            this._updateWidgetTabOrder(experience);

            // When a scene is added, switch to it
            this.listenTo(experience, "add:scenes", function(scene) {
                this._forceSetSceneNumber(scene.get("sceneNumber"));
            });

            // When a scene is removed, ensure the scene number is valid and events are triggered
            this.listenTo(experience, "remove:scenes", function(scene) {
                const deletedSceneNumber = parseInt(scene.get("sceneNumber"), 10);
                const currentSceneNumber = parseInt(this.get("sceneNumber"), 10);

                // If the removed scene is after the current scene, we don't have to do anything
                // If the removed scene is the current scene, it depends on whether it was the last scene
                // If the removed scene is before the current scene, we decrement the scene number to preserve the current scene
                if (deletedSceneNumber === currentSceneNumber) {
                    this._forceSetSceneNumber(Math.max(
                        1,
                        Math.min(experience.get("scenes").length, deletedSceneNumber)
                    ));
                } else if (deletedSceneNumber < currentSceneNumber) {
                    this.set("sceneNumber", this.get("sceneNumber") - 1);
                }
            });

            // Update current scene number if needed, when scenes are swapped/moved
            this.listenTo(experience, "scenes:swapped", function(options) {
                const currentSceneNumber = this.get("sceneNumber");

                if (options.a.fromSceneNumber === currentSceneNumber) {
                    this.set("sceneNumber", options.a.toSceneNumber);
                }

                if (options.b.fromSceneNumber === currentSceneNumber) {
                    this.set("sceneNumber", options.b.toSceneNumber);
                }
            });

            // Fetch the last visit hash for this experience, before starting to register this session's views
            const lastVisit = LoggedInUserState.getLastExperienceVisit(experienceId);
            this.set("lastVisit", lastVisit);

            // Log initial experience and scene views
            LoggedInUserState.registerExperienceVisit(experienceId);
            LoggedInUserState.registerSceneVisit(experienceId, scene.get("id"));

            // Register the scene change handler and invoke it now
            this.listenTo(this, "change:scene", this._sceneChangeHandler);
            this._sceneChangeHandler();

            this.selection = new WidgetSelectionViewModel(null, {editorViewModel: this});

            // If there are comments on the experience, fetch the user collection since we will need it for colors
            // Also disable the comments layer, it will be enabled again if there are unread comments
            if (experience.get("lastCommentedAt")) {
                LoggedInUserState.fetchGlobalCollection(UserCollection);
                // Lookup "show comments" state
                this.set("showComments", Storage.Local.get(Storage.Keys.COMMENTS, "showComments", false));
            }

            // Initialize comments and parse to collections and models
            const experienceCommentsJSON = this.get("experienceCommentsJSON");
            const collection = new CommentCollection();
            const sceneIDs = [];

            // Store comments
            this.set("comments", collection, {silent: true});

            _.each(experienceCommentsJSON, (sceneCommentsJSON, sceneID) => {
                collection.add(sceneCommentsJSON, {parse: true, sceneID});
                sceneIDs.push(sceneID);
            });

            // Loop through all comments to compute the initial number of unread comments per experience, scene, and thread
            let experienceUnread = 0;

            _.each(sceneIDs, (sceneID) => {
                const sceneComments = collection.getCommentsForScene(sceneID);
                const lastSceneVisit = lastVisit.sceneVisits[sceneID] || 0;
                let sceneUnread = 0;

                _.each(sceneComments, (sceneComment) => {
                    const unreadComments = sceneComment.getUnreadComments(lastSceneVisit);
                    sceneComment.set("unread", unreadComments, {computedProperty: true});
                    sceneUnread += unreadComments;
                    experienceUnread += unreadComments;
                });

                const foundScene = experience.getSceneById(sceneID);

                if (foundScene) {
                    foundScene.set("unread", sceneUnread, {computedProperty: true});
                }
            });

            experience.set("unread", experienceUnread, {computedProperty: true});

            // Set unread on all remaining scenes to 0
            const setDefaultUnread = function(scene) {
                if (!scene.get("unread")) {
                    scene.set("unread", 0, {computedProperty: true});
                }
            };

            experience.get("scenes").each(setDefaultUnread);

            setDefaultUnread(experience.get("overlayScene"));
            setDefaultUnread(experience.get("backgroundScene"));

            // Mark scene comments as read when the scene changes with the comment layer active
            this.listenTo(this, "change:scene", function() {
                if (!this.get("showComments")) {
                    return;
                }

                const previousScene = this.previous("scene");

                if (previousScene) {
                    this.markSceneCommentsRead(previousScene);
                }
            });

            // Mark scene comments as read when the comment layer is deactivated
            this.listenTo(this, "change:showComments", function() {
                if (this.get("showComments") === false && this.previous("showComments") === true) {
                    this.markSceneCommentsRead(this.get("scene"));
                }
            });

            // TODO: Consider creating a class for dealing with "last used properties" (EditorState) ?

            const scale = Storage.Local.get(Storage.Keys.EDITOR, `${experienceId}-scaleFactor`, 1.0);
            this.get("zoomModel").set("scale", scale, {silent: true});

            // Keep track of last used scene size
            this.listenTo(experience, "change:width change:height", () => {
                Storage.Local.update(Storage.Keys.EDITOR, "experienceSize", {
                    width: experience.get("width"),
                    height: experience.get("height")
                });
            });

            // Keep track of rulers shown setting.
            this.set("rulersShown", Storage.Local.get(Storage.Keys.RULERS_SHOWN, "rulersShown", true));

            this.listenTo(this, "change:rulersShown", (model, value) => {
                Storage.Local.update(Storage.Keys.RULERS_SHOWN, {rulersShown: value});
            });

            // Keep track of Master Layer setting
            this.set("showMasterLayers", Storage.Local.get(Storage.Keys.MASTER_LAYERS, "showMasterLayers", true));

            this.listenTo(this, "change:showMasterLayers", (model, value) => {
                Storage.Local.update(Storage.Keys.MASTER_LAYERS, {showMasterLayers: value});
            });

            // Keep track of snapping enabled setting.
            this.set("snappingEnabled", Storage.Local.get(Storage.Keys.SNAPPING_ENABLED, "snappingEnabled", false));

            this.listenTo(this, "change:snappingEnabled", (model, value) => {
                Storage.Local.update(Storage.Keys.SNAPPING_ENABLED, {snappingEnabled: value});
            });

            // Store "show comments" state locally
            this.listenTo(this, "change:showComments", (model, value) => {
                Storage.Local.update(Storage.Keys.COMMENTS, {showComments: value});
            });
        },

        /**
         * @private
         *
         * Registers/adds a new comment. At this point we expect that the passed scene has
         * been saved and contains an ID.
         *
         * @param  {Comment} comment    The comment to add.
         * @param  {Scene} scene        The scene model associated with the comment.
         */
        _registerNewComment(comment, scene) {
            const currentSceneID = scene.get("id");

            // Ensure the comments layer is active
            this.set("showComments", true);

            // New comments need a reference to the experience and scene in order to save them
            comment.experience = this.get("experience").get("id");
            comment.sceneID = currentSceneID;
            // For backward compatibility
            comment.scene = currentSceneID;

            this.get("comments").add(comment);
        },

        /**
         * Adds a comment for the current scene.
         *
         * @param {Comment} comment The new comment to add.
         */
        addComment(comment) {
            const scene = this.get("scene");

            if (scene.has("id")) {
                this._registerNewComment(comment, scene);
            } else {
                // The scene might not have been saved yet, wait for the ID to arrive.
                this.listenToOnce(scene, "change:id", () => {
                    this._registerNewComment(comment, scene);
                });
            }
        },

        getCurrentComments() {
            const currentSceneID = this.get("scene").get("id");

            return this.get("comments").getCommentsForScene(currentSceneID);
        },

        /**
         * Invoked when a comment is posted.
         *
         * @param {Comment} comment The root comment.
         * @param {Promise} promise The promise for the comment save.
         */
        commentPosted(comment, promise) {
            const now = Time.server();
            // Adjust unread state
            this._commentRead(comment);

            // Stamp the last commented at
            this.get("scene").set("lastCommentedAt", now, {triggeredByServerUpdate: true});
            this.get("experience").set("lastCommentedAt", now, {triggeredByServerUpdate: true});

            // Load the server's updated lastCommentedAt values when the promise is done
            promise.done(this._fetchLastCommentedAt.bind(this, this.get("scene").get("id")));

            // Record a visit to the scene.
            // This is a bit unfortunate, since it means that new comments posted by others after the experience was
            // opened will not be flagged. But this is better than flagging the user's own comments as new, and we
            // don't support concurrent editing right now anyway.
            // We add an offset to the timestamp, since the server will stamp a slightly later time.
            LoggedInUserState.registerExperienceVisit(this.get("experience").get("id"), 5000);
            LoggedInUserState.registerSceneVisit(this.get("experience").get("id"), this.get("scene").get("id"));

            this.get("lastVisit").visit = now + 5000;
            this.get("lastVisit").sceneVisits[this.get("scene").get("id")] = this.get("lastVisit").visit;
        },

        /**
         * Marks the comments on the given scene as read.
         * @param {Scene} scene The scene.
         */
        markSceneCommentsRead(scene) {
            if (scene.get("unread") > 0) {
                // Update counters
                this.get("experience").set("unread", this.get("experience").get("unread") - scene.get("unread"), {computedProperty: true});
                scene.set("unread", 0, {computedProperty: true});

                // Update comment counters and flags
                this.get("comments").each((comment) => {
                    if (comment.get("unread") > 0) {
                        comment.set("unread", 0, {computedProperty: true});

                        comment.set("isUnread", false, {computedProperty: true});
                        comment.get("replies").each((reply) => {
                            reply.set("isUnread", false, {computedProperty: true});
                        });
                    }
                });
            }
        },

        /**
         * Invoked when a comment is deleted.
         * @param {Comment} comment The comment that has been deleted.
         * @param {Promise} promise The promise for the comment deletion.
         */
        commentDeleted(comment, promise) {
            // Ensure the unread counter is adjusted
            this._commentRead(comment);

            // Load the server's updated lastCommentedAt values when the promise is done
            promise.done(this._fetchLastCommentedAt.bind(this, this.get("scene").get("id")));
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/editor/EditorViewModel.js